"use client";

import * as React from "react";
import {
  useState, useEffect, useMemo, useContext, createContext,
} from "react";
import { useSearchParams } from "next/navigation";

import {
  deepEqual,
} from "@audacia-hq/shared/utils";

import {
  ExpertFilters, ExpertFrontFilters, ExpertIdentifier, ExpertOrderBy,
} from "../models/expert";
import { getExpertsList } from "../services/api";
import {
  ExpertOrderings, defaultExpertFilters, defaultExpertFrontFilters, getOrderedList,
} from "../services/expertUtils";

import { useLocale } from "./LocaleContext";
import { useExpert } from "./ExpertContext";
import { useMember } from "./MemberContext";

interface ExpertListContextType {
  expertsList: ExpertIdentifier[];
  expertsListLoading: boolean,
  expertFilters: ExpertFilters;
  expertFrontFilters: ExpertFrontFilters;
  expertOrderBy: ExpertOrderBy;
  setExpertFilters: (filters: ExpertFilters) => void;
  setExpertFrontFilters: (filters: Partial<ExpertFrontFilters>) => void;
  setExpertOrderBy: (orderBy: ExpertOrderBy) => void;
  clearFilters: (valuesToSet?: { filters?: any, frontFilters?: any }) => void;
}

const ExpertListContext = createContext<ExpertListContextType>({
  expertsList: [],
  expertsListLoading: true,
  expertFilters: defaultExpertFilters,
  expertFrontFilters: defaultExpertFrontFilters,
  expertOrderBy: ExpertOrderBy.DEFAULT,
  setExpertFilters: (e) => undefined,
  setExpertFrontFilters: (e) => undefined,
  setExpertOrderBy: (e) => undefined,
  clearFilters: () => undefined,
});

const ExpertListProvider: React.FC<{
  children: React.ReactNode;
  initialOrderings: ExpertOrderings;
}> = ({
  children, initialOrderings,
}) => {
  const language = useLocale();

  const { expertIdentifiers } = useExpert();
  const { member } = useMember();

  const favoriteSet = new Set(member?.profile?.favorites || []);
  const consultedSet = new Set(member?.consultedExperts || []);

  const params = useSearchParams();

  const [expertFilters, setExpertFilters] = useState<ExpertFilters>(structuredClone(defaultExpertFilters));
  const [expertFrontFilters, setExpertFrontFilters] = useState<ExpertFrontFilters>(structuredClone({
    ...defaultExpertFrontFilters,
    expertType: [
      ...(defaultExpertFrontFilters.expertType || []),
      ...(params && params.get("favorite") !== null ? ["FAVORITE" as const] : []),
      ...(params && params.get("new") !== null ? ["NEW" as const] : []),
    ],
  }));
  const [expertOrderBy, setExpertOrderBy] = useState<ExpertOrderBy>(ExpertOrderBy.DEFAULT);

  const [orderings, setOrderings] = useState<ExpertOrderings>(initialOrderings);
  const [list, setList] = useState<ExpertIdentifier[]>(getOrderedList(orderings, expertIdentifiers, favoriteSet, consultedSet, expertFrontFilters, expertOrderBy));

  const [listLoading, setListLoading] = useState<boolean>(false);

  const clearFilters = (valuesToSet) => {
    setExpertFilters({ ...structuredClone(defaultExpertFilters), ...(valuesToSet?.filters || {}) });
    setExpertFrontFilters({ ...structuredClone({ ...defaultExpertFrontFilters, expertType: [] }), ...(valuesToSet?.frontFilters || {}) });
  };

  const mapOrderBy = (orderBy: ExpertOrderBy) => (
    [
      ExpertOrderBy.CONSULTATION,
      ExpertOrderBy.EVALUATION, ExpertOrderBy.LONGEVITY, ExpertOrderBy.NEWEST,
      ExpertOrderBy.LIVECHAT_CONSULTATION, ExpertOrderBy.PHONE_CONSULTATION,
      ExpertOrderBy.WRITTEN_CONSULTATION, ExpertOrderBy.VIDEO_CONSULTATION,
    ].includes(orderBy)
      ? orderBy
      : undefined
  );

  const loadExpertsList = (expertFilters: ExpertFilters & { orderBy: ExpertOrderBy } & { profileLanguage: string }) => {
    setListLoading(true);
    getExpertsList({
      variables: { expertFilters },
    })
      .then(({ data }) => {
        const newOrderings = { ...orderings, filtered: data.expertsList };
        setList(getOrderedList(newOrderings, expertIdentifiers, favoriteSet, consultedSet, expertFrontFilters, expertOrderBy));
        setOrderings(newOrderings);
      })
      .finally(() => setListLoading(false));
  };

  useEffect(() => {
    if (deepEqual(expertFilters, defaultExpertFilters) && [ExpertOrderBy.DEFAULT, ExpertOrderBy.ALPHABETICAL].includes(expertOrderBy)) {
      setList(getOrderedList(orderings, expertIdentifiers, favoriteSet, consultedSet, expertFrontFilters, expertOrderBy));
    }
    loadExpertsList({
      ...expertFilters,
      orderBy: mapOrderBy(expertOrderBy),
      profileLanguage: language.toUpperCase(),
    });
  }, [expertFilters, expertOrderBy]);

  useEffect(() => {
    setList(getOrderedList(orderings, expertIdentifiers, favoriteSet, consultedSet, expertFrontFilters, expertOrderBy));
  }, [expertFrontFilters]);

  const value = useMemo(
    () => (
      {
        expertsList: list,
        expertsListLoading: listLoading,
        expertFilters,
        expertFrontFilters,
        expertOrderBy,
        setExpertFilters: (newValue) => setExpertFilters((prev) => (deepEqual(prev, newValue) ? prev : newValue)),
        setExpertFrontFilters: (partial) => setExpertFrontFilters((prev) => {
          const newValue = { ...prev, ...partial };
          return deepEqual(prev, newValue) ? prev : newValue;
        }),
        setExpertOrderBy,
        clearFilters,
      }),
    [list, listLoading, expertFilters, expertFrontFilters, expertOrderBy,
      setExpertFilters, setExpertFrontFilters, setExpertOrderBy, clearFilters],
  );

  return (
    <ExpertListContext.Provider value={value}>
      {children}
    </ExpertListContext.Provider>
  );
};

const useExpertList = () => useContext(ExpertListContext);

export default ExpertListContext;
export {
  ExpertListProvider, useExpertList,
};
